import ModalViewModel from 'infra/ModalViewModel'
import { action, computed, makeObservable, observable } from 'mobx'
import { Id, Router } from 'types/definitions'
import JobCreationRootViewModel from 'jobs/creation/JobCreationRootViewModel'
import { PrefilledDraftInput } from 'jobs/creation/PrefilledDraftInput'
import { task } from 'mobx-task'
import link from 'infra/link'
import { AlertFactory, AlertState } from 'alerts/Alert'
import SubmissionViewModel from 'jobs/creation/SubmissionViewModel'
import { QuestionStepViewModel } from 'jobs/creation/IntakeQuestionsViewModel'

export type ModalStep = 'Questions' | 'Billing'
export type ModalType = 'NewDraft' | 'EditDraft'

export interface ModalHeader {
  title: string
  description: string
  pager: string | null
}

export default class CreateJobDialogViewModel extends ModalViewModel {
  @observable modalStep: ModalStep = 'Questions'
  @observable modalType: ModalType = 'NewDraft'
  @observable jobIds: Id[] = []
  @observable currentJobIndex: number = 0

  constructor(
    private router: Router,
    private newAlert: AlertFactory,
    public jobCreationRootVM: JobCreationRootViewModel,
  ) {
    super()
    makeObservable(this)

    this.close$.subscribe(() => this.reset())

    this.jobCreationRootVM.jobCreated$
      .pipe(this.whileShowing())
      .subscribe(() => {
        this.newAlert()
          .saying('Success!', 'Job was created')
          .withState(AlertState.Success)
          .autoDismiss()
          .show()
      })
  }

  @computed
  get editor() {
    return this.jobCreationRootVM.current
  }

  @computed
  get isBillingStepReady() {
    return this.editor.outline.stepsContainers.includes(this.editor.submission)
  }

  @computed
  get jobWasCreated() {
    return this.editor.jobWasCreated
  }

  @computed
  get isBillingRelevant() {
    if (!this.editor.job.owner.isCurrentUser) {
      return false
    }

    const submission = this.editor.outline.stepsContainers.find(
      (stepContainer): stepContainer is SubmissionViewModel =>
        stepContainer === this.editor.submission,
    )

    return submission?.billing.isBillingRelevant
  }

  @computed
  get draftTitle() {
    return this.editor.job.displayName.trim()
  }

  @computed
  get modalHeader(): ModalHeader {
    if (this.modalStep === 'Billing') {
      return {
        title: 'Add payment details',
        description: 'Select payment method and review summary',
        pager: '2 of 2',
      }
    }

    const pager =
      this.isBillingRelevant && this.modalStep === 'Questions'
        ? '1 of 2'
        : this.jobIds.length > 1
          ? `${this.currentJobIndex + 1} of ${this.jobIds.length}`
          : null

    return {
      title: this.modalType === 'NewDraft' ? 'Create Job' : 'Edit responses',
      description: 'We will automatically save your progress.',
      pager,
    }
  }

  @computed
  get canViewDetail() {
    return this.modalType === 'NewDraft' && this.editor.jobWasCreated
  }

  @computed
  get incompleteQuestionsCount() {
    return this.editor.outline.stepsContainers.filter(
      (sc) =>
        sc instanceof QuestionStepViewModel &&
        sc.question.type !== 'BREAKER' &&
        sc.question.type !== 'DOCUMENTS' &&
        !sc.step.valid,
    ).length
  }

  @computed
  get bulkEditActionButtonText() {
    return this.currentJobIndex + 1 === this.jobIds.length ? 'Complete' : 'Next'
  }

  @action
  reset() {
    this.modalStep = 'Questions'
    this.modalType = 'NewDraft'
    this.currentJobIndex = 0
    this.jobIds = []
  }

  @action
  open(jobIds?: Id[], prefilledDraftInput?: PrefilledDraftInput) {
    const jobId = jobIds?.[0]
    this.jobIds = jobIds || []
    this.show()
    this.modalType = jobIds ? 'EditDraft' : 'NewDraft'
    return this.setEditor(jobId, prefilledDraftInput)
  }

  @action
  setEditor(jobId?: Id, prefilledDraftInput?: PrefilledDraftInput) {
    return this.edit(jobId, prefilledDraftInput)
  }

  @action
  setStep(step: ModalStep) {
    this.modalStep = step
  }

  @action
  goDetail() {
    this.close()
    this.router.push(link.job(Id.assert(this.editor.job.id)))
  }

  edit = task(async (jobId?: Id, prefilledDraftInput?: PrefilledDraftInput) => {
    return await this.jobCreationRootVM.edit(
      jobId,
      prefilledDraftInput ?? PrefilledDraftInput.empty(),
    )
  })

  @action.bound
  nextJob() {
    this.currentJobIndex = this.currentJobIndex + 1

    if (this.currentJobIndex === this.jobIds.length) {
      this.close()

      this.newAlert()
        .withState(AlertState.Success)
        .saying('Great Job!', `You've successfully edited the job responses`)
        .autoDismiss()
        .show()

      return
    }

    this.setEditor(this.jobIds[this.currentJobIndex])
  }

  @action.bound
  previousJob() {
    this.currentJobIndex = this.currentJobIndex - 1
    this.setEditor(this.jobIds[this.currentJobIndex])
  }
}
